import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineRefresh, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import {
    // updateApiRecord,
    deleteApiRecord,
} from 'services/XeroService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { Button } from 'components/Form'
import { syncIncrementalXeroData } from 'services/XeroIntegrationService'
// import { companyStatus } from 'components/helper'
injectReducer('xero', reducer)

// Format currency
const formatCurrency = (amount, currency = 'AUD') => {
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency,
    }).format(amount || 0)
}

// Format date
const formatDate = dateString => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-AU')
}

// Status badge component
const StatusBadge = ({ status }) => {
    const getStatusColor = status => {
        switch (status?.toUpperCase()) {
            case 'DRAFT':
                return 'bg-gray-100 text-gray-800'
            case 'SUBMITTED':
                return 'bg-blue-100 text-blue-800'
            case 'AUTHORISED':
                return 'bg-green-100 text-green-800'
            case 'PAID':
                return 'bg-emerald-100 text-emerald-800'
            case 'VOIDED':
                return 'bg-red-100 text-red-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(status)}`}>{status || 'UNKNOWN'}</span>
}

export default function Xero() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(state => state.xero.data)
    const { company, customer } = useSelector(state => state.filterData)
    const [syncLoading, setSyncLoading] = useState(false)

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        company,
        customer,
    ])

    const fetchData = () => dispatch(getData({ ...tableData, company: company, customer: customer }))

    // Simple sync function for the top button - no company ID needed
    const handleSync = () => {
        setSyncLoading(true)
        syncIncrementalXeroData()
            .then(response => {
                Success('Incremental sync initiated. This will sync data modified in the last 2 days.')
            })
            .catch(error => {
                console.error('Error syncing Xero data:', error)
                const errorMessage = error.response?.data?.message || 'Failed to sync Xero data'
                Error(errorMessage)
            })
            .finally(() => {
                setSyncLoading(false)
            })
    }

    const handleDelete = id => {
        deleteApiRecord(id)
            .then(() => {
                Success('Invoice Deleted Successfully!')
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    // const handleStatus = (id, data) => {
    //     updateApiRecord(id, data)
    //         .then(() => {
    //             Success('Invoice Status Updated Successfully!')
    //             fetchData()
    //         })
    //         .catch(error => console.log('Error: ', error))
    // }

    const columns = useMemo(
        () => [
            {
                field: 'invoice_number',
                header: 'Invoice Number',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => (
                    <div className="font-medium">
                        {row.invoice_number || row.reference || `Draft-${row.invoice_id?.slice(0, 8)}`}
                    </div>
                ),
            },
            {
                field: 'contact_name',
                header: 'Customer',
                className: 'min-w-[180px]',
                hide: false,
                renderCell: row => (
                    <div>
                        <div className="font-medium">{row.contact_name || 'No Contact'}</div>
                        <div className="text-sm text-gray-500">ID: {row.contact ? row.contact.slice(0, 8) : 'N/A'}</div>
                    </div>
                ),
            },
            {
                field: 'company_name',
                header: 'Company',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => <div className="font-medium text-sm">{row.company_name || 'No Company'}</div>,
            },
            {
                field: 'reference',
                header: 'Reference',
                className: 'min-w-[120px]',
                hide: false,
                renderCell: row => row.reference || '-',
            },
            {
                field: 'date',
                header: 'Invoice Date',
                className: 'min-w-[120px]',
                hide: false,
                renderCell: row => formatDate(row.date),
            },
            {
                field: 'due_date',
                header: 'Due Date',
                className: 'min-w-[120px]',
                hide: false,
                renderCell: row => formatDate(row.due_date),
            },
            {
                field: 'total',
                header: 'Amount',
                className: 'min-w-[120px]',
                hide: false,
                renderCell: row => <div className="font-medium">{formatCurrency(row.total, row.currency_code)}</div>,
            },
            {
                header: 'Status',
                field: 'status',
                hide: false,
                className: 'min-w-[120px]',
                renderCell: row => <StatusBadge status={row.status} />,
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4 min-w-[100px]',
                hide: false,
                renderCell: row => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission('xero', 'edit') ? (
                                <Tooltip title={'Edit Invoice'}>
                                    <Link to={`/finacials/xero/${row.invoice_id}/edit`} className="hover:text-primary">
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission('xero', 'delete') ? (
                                <Tooltip title={'Delete Invoice'}>
                                    <ConfirmationModal
                                        message={'Are you sure you want to delete this invoice?'}
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            company,
            customer,
        ] // Dependencies for useMemo to avoid unnecessary re-renders
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Xero Invoice Management</h2>
                {/* Incremental Sync button */}
                <Tooltip title="Incremental sync (last 2 days)">
                    <Button
                        variant="twoTone"
                        color="blue-600"
                        onClick={handleSync}
                        disabled={syncLoading}
                        className="flex items-center gap-2"
                    >
                        <HiOutlineRefresh className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                        {syncLoading ? 'Syncing...' : 'Incremental Sync'}
                    </Button>
                </Tooltip>
            </div>
            <Table
                loading={loading}
                filter={true}
                datas={data || []}
                columns={columns}
                add_new={checkPermission('xero', 'create') ? '/finacials/xero/create' : ''}
                pagingData={tableData}
                onChange={e => dispatch(setTableData(e))}
                // Status tabs removed as we only show Maestro-created draft invoices
            />
        </>
    )
}
